import React from 'react';
import { cn } from '@/lib/utils';

export interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'body' | 'body-sm' | 'body-lg' | 'code';
  color?: 'default' | 'muted' | 'primary' | 'warning' | 'success' | 'danger';
  as?: React.ElementType;
}

const Typography: React.FC<TypographyProps> = ({
  className,
  variant = 'body',
  color = 'default',
  as: Component = 'p',
  children,
  ...props
}) => {
  const variants = {
    h1: 'text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight',
    h2: 'text-3xl md:text-4xl font-bold tracking-tight',
    h3: 'text-2xl md:text-3xl font-semibold',
    h4: 'text-xl md:text-2xl font-semibold',
    h5: 'text-lg font-semibold',
    h6: 'text-base font-semibold',
    body: 'text-base',
    'body-sm': 'text-sm',
    'body-lg': 'text-lg',
    code: 'font-mono text-sm bg-surface-100 px-2 py-0.5 rounded',
  };

  const colors = {
    default: 'text-surface-900',
    muted: 'text-surface-500',
    primary: 'text-primary-600',
    warning: 'text-warning-600',
    success: 'text-green-600',
    danger: 'text-red-600',
  };

  return (
    <Component
      className={cn(variants[variant], colors[color], className)}
      {...props}
    >
      {children}
    </Component>
  );
};

export const H1: React.FC<TypographyProps> = (props) => (
  <Typography variant="h1" {...props} />
);

export const H2: React.FC<TypographyProps> = (props) => (
  <Typography variant="h2" {...props} />
);

export const H3: React.FC<TypographyProps> = (props) => (
  <Typography variant="h3" {...props} />
);

export const H4: React.FC<TypographyProps> = (props) => (
  <Typography variant="h4" {...props} />
);

export const Body: React.FC<TypographyProps> = (props) => (
  <Typography variant="body" {...props} />
);

export const BodySm: React.FC<TypographyProps> = (props) => (
  <Typography variant="body-sm" {...props} />
);

export const BodyLg: React.FC<TypographyProps> = (props) => (
  <Typography variant="body-lg" {...props} />
);

export const Text: React.FC<TypographyProps> = (props) => (
  <Typography variant="body" {...props} />
);

export const Code: React.FC<TypographyProps> = (props) => (
  <Typography variant="code" {...props} />
);

export { Typography };
